import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { LinkIt, urlRegex } from 'react-linkify-it';
import { FancySharePublicDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ProfileRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackerExtraInfo, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ellipsis } from '@diritto/common/utils/string';
import { FancyShareAction } from 'constants/fancyShare';
import { isPC } from 'constants/layout';
import { EMPTY_PROFILE_TEXT } from 'constants/profile';
import { ZIndex } from 'constants/zIndex';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { AdultRoundIcon } from 'icons/AdultRoundIcon';
import { ArrowRightSlimIcon } from 'icons/ArrowRightSlimIcon';
import { LogoIcon } from 'icons/LogoIcon';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { FancyShareReaction } from 'app-components/fancyShare/FancyShareReaction';
import { FancyShareDispatchParams } from 'state-hooks/fancyShare/useFancyShareDispatch';
import { getTextLineStyle } from 'styles/common';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { IkImage } from 'ui-components/Imagekit/IkImage';

interface FancyShareContentItemProps {
  fancyShare: FancySharePublicDto;
  profile?: ProfileRelayDto;
  authorProfile?: ProfileRelayDto;
  content?: ContentPublicRelayDto;
  episode?: EpisodePublicRelayDto;
  isLiked: boolean;
  onDispatchAction: (params: FancyShareDispatchParams) => void;
  trackerExtraInfo: TrackerExtraInfo;
  showMenuButton?: boolean;
  onClick?: () => void;
}

const makeExternalLink = (match: string, key: number) => (
  <ExternalLink href={match} key={key}>
    <StyledLink>{match}</StyledLink>
  </ExternalLink>
);

const getActions = ({
  myProfileId,
  authorProfileId,
  fancyShareProfileId,
}: {
  myProfileId?: number;
  authorProfileId?: number;
  fancyShareProfileId: number;
}) => {
  // 로그인 안한 상태
  if (!myProfileId) {
    return [FancyShareAction.Share];
  }

  // 내 작품
  if (authorProfileId === myProfileId) {
    return fancyShareProfileId === myProfileId
      ? [FancyShareAction.Share, FancyShareAction.DownloadImage, FancyShareAction.ActiveEdit, FancyShareAction.Delete]
      : [FancyShareAction.Share, FancyShareAction.DownloadImage];
  }

  return fancyShareProfileId === myProfileId
    ? [FancyShareAction.Share, FancyShareAction.ActiveEdit, FancyShareAction.Delete]
    : [FancyShareAction.Share];
};

export const FancyShareItem = ({
  fancyShare,
  profile,
  authorProfile,
  content,
  episode,
  isLiked,
  trackerExtraInfo,
  onDispatchAction,
  onClick,
  showMenuButton = true,
}: FancyShareContentItemProps) => {
  const { tracker } = useTrackingState();

  const handleClickToContent = useCallback(
    (contentId: number) => {
      tracker.track(TrackingEventType.ClickToContentDetail, { contentId, trackerExtraInfo });
    },
    [tracker, trackerExtraInfo],
  );

  const handleClickToEpisode = useCallback(
    (episodeId: number) => {
      tracker.track(TrackingEventType.ClickToEpisodeViewer, { episodeId, trackerExtraInfo });
    },
    [tracker, trackerExtraInfo],
  );

  const { profile: myProfile, includeAdult } = useAuthState();
  const actions = getActions({
    fancyShareProfileId: fancyShare.profileId,
    myProfileId: myProfile?.id,
    authorProfileId: authorProfile?.id,
  });

  return (
    <StyledContainer onClick={onClick}>
      <StyledImageLink href={`/fancy-shares/${fancyShare.id}`}>
        {fancyShare.isAdult && !includeAdult ? (
          <StyledAdultBlock>
            <StyledLogoIconWrapper>
              <LogoIcon color={Color.White} size={110} />
            </StyledLogoIconWrapper>
            <StyledAdultRoundIconWrapper>
              <AdultRoundIcon className="size-[16px]" fillOpacity={0} />
            </StyledAdultRoundIconWrapper>
            <StyledBlurIkImage
              transform={{
                path: fancyShare.file.path,
                isPrivate: fancyShare.file.isPrivate,
                transformation: ImageTransformOptions[ImageTransformType.FancyShare],
              }}
              alt={fancyShare.description}
              fullWidth
            />
          </StyledAdultBlock>
        ) : (
          <StyledImageWrapper backgroundColor={Color.Gray200}>
            <StyledIkImage
              transform={{
                path: fancyShare.file.path,
                isPrivate: fancyShare.file.isPrivate,
                transformation: ImageTransformOptions[ImageTransformType.FancyShare],
              }}
              alt={fancyShare.description}
              fullWidth
            />
          </StyledImageWrapper>
        )}
      </StyledImageLink>

      <StyledFancyShareWrapper>
        <StyledProfile>
          {profile && !profile.isDeleted ? (
            <Link href={`/accounts/profiles/${profile.id}`}>
              <ProfileImage file={profile.profileImage} defaultImageKey={profile.nickname} size={32} />
            </Link>
          ) : (
            <ProfileImage size={32} />
          )}
        </StyledProfile>
        <StyledFancyShareInfo>
          <StyledTop>
            <StyledName>
              {profile && profile?.isDeleted && EMPTY_PROFILE_TEXT}
              {profile && !profile.isDeleted && (
                <Link href={`/accounts/profiles/${profile.id}`}>{profile.nickname}</Link>
              )}
            </StyledName>
            <StyledDate date={fancyShare.createdAt} />
          </StyledTop>
          {fancyShare.description && (
            <StyledDescription>
              <LinkIt component={makeExternalLink} regex={urlRegex}>
                {fancyShare.description}
              </LinkIt>
            </StyledDescription>
          )}
          <StyledLinkWrapper>
            {content && (
              <Link href={`/contents/${content.id}`} onClick={() => handleClickToContent(content.id)}>
                <StyledEpisodeTitle>{ellipsis(content.title, 15)}</StyledEpisodeTitle>
              </Link>
            )}
            {content && episode && (
              <>
                <StyledArrowRightSlimIconWrapper>
                  <ArrowRightSlimIcon size={12} color={Color.Gray300} />
                </StyledArrowRightSlimIconWrapper>
                <Link
                  href={`/contents/${episode.contentId}/episodes/${episode.id}`}
                  onClick={() => handleClickToEpisode(episode?.id)}>
                  <StyledEpisodeTitle>{ellipsis(episode.title, 15)}</StyledEpisodeTitle>
                </Link>
              </>
            )}
          </StyledLinkWrapper>
          <FancyShareReaction
            fancyShare={fancyShare}
            isLiked={isLiked}
            replyCount={0}
            isShowReplyWrite={false}
            isShowReplyCount={false}
            showMenuButton={showMenuButton}
            actions={actions}
            content={content}
            authorProfile={!authorProfile?.isDeleted ? authorProfile : undefined}
            onDispatchAction={onDispatchAction}
          />
        </StyledFancyShareInfo>
      </StyledFancyShareWrapper>
      <StyledDivider />
    </StyledContainer>
  );
};

const StyledImageLink = styled(Link)`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (${isPC}) {
    max-width: 414px;
  }
`;

const StyledLogoIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${ZIndex.FancyShareInput};
  opacity: 0.5;
`;

const StyledAdultRoundIconWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: ${ZIndex.FancyShareInput};
  opacity: 0.9;
`;

const StyledAdultBlock = styled.div`
  background-color: ${Color.Black};
  border-radius: 4px;
  width: 100%;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
  padding: 18px;
`;

const StyledBlurIkImage = styled(IkImage)`
  filter: blur(40px);
  transform: translate3d(0, 0, 0);
  aspect-ratio: 1 / 1;
`;

const StyledIkImage = styled(IkImage)`
  border-radius: 4px;
  aspect-ratio: 1 / 1;
`;

const StyledFancyShareWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledArrowRightSlimIconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
`;

const StyledLinkWrapper = styled.div`
  display: flex;
  margin-top: 4px;
  flex-direction: row;
  align-items: center;
`;

const StyledLink = styled.span`
  color: ${Color.Blue500};
  text-decoration: underline;
  word-break: break-all;
  white-space: break-spaces;
  word-wrap: anywhere;
`;

const StyledDivider = styled.div`
  margin-top: 16px;
  width: 100%;
  height: 1px;
  background-color: ${Color.Gray200};
`;

const StyledProfile = styled.div`
  flex-shrink: 0;
`;

const StyledFancyShareInfo = styled.div`
  width: calc(100% - 32px - 16px - 16px);
  margin-left: 16px;
  position: relative;
  flex-grow: 1;
`;

const StyledTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  ${Typography.XXSmall};

  & > a {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }

  & > p {
    margin-right: 4px;
    width: fit-content;
    font-weight: ${FontWeight.Bold};
    ${getTextLineStyle(1)};
  }
`;

const StyledDate = styled(RelativeDateText)`
  width: fit-content;
  margin-left: 10px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  ${getTextLineStyle(1)};
`;

const StyledEpisodeTitle = styled.p`
  font-weight: ${FontWeight.Medium};
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
  word-break: break-all;
  white-space: break-spaces;
`;

const StyledDescription = styled.p`
  margin-top: 4px;
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray900};
  ${Typography.XXSmall}
  word-break: break-all;
  white-space: break-spaces;
`;

const StyledImageWrapper = styled.div(
  ({ backgroundColor = Color.White }: { backgroundColor?: string }) => css`
    width: 100%;
    display: flex;
    background-color: ${backgroundColor};
  `,
);
